import { ExclamationCircleFilled } from '@ant-design/icons';
import { Input } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import HowItWorksModal from '@/components/claimBookComp/HowItWorksModal';
import RedeemErrorModal from '@/components/claimBookComp/RedeemErrorModal';
import SuccessModal from '@/components/claimBookComp/SuccessModal';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { authModalControl } from '@/atoms/authModalControl';
import { modalOpenErrorAtom } from '@/atoms/sync-amazon-order/modalOpenError';
import { successFullModalAtom } from '@/atoms/sync-amazon-order/successFullModalAtom';
import { user } from '@/atoms/user';
import { checkAmazonOrderId, syncAmazonOrderId } from '@/services/book-claim';
import { User } from '@/utils/interfaces/User';

const ClaimBookPage = () => {
  const [orderId, setOrderId] = useState('');
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useRecoilState(modalOpenErrorAtom);
  const [howItWorkModalOpen, setHowItWorkModalOpen] = useState(false);
  const [successFullModalOpen, setSuccessFullModalOpen] =
    useRecoilState(successFullModalAtom);

  const userData: User | undefined = useRecoilValue(user);
  const [, setAuthModal] = useRecoilState(authModalControl);
  const router = useRouter();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderId(e.target.value);
    if (error) setError('');
  };

  const showSignupModal = () => {
    setAuthModal((prev) => ({
      ...prev,
      login: false,
      signUp: true,
      headingCopy: 'Sign up',
      subHeadingCopy: 'Sign up required to claim digital book',
      amazonOrderId: orderId,
      noRedirect: true,
    }));
  };

  const handleSyncAmazonOrderId = async (id: string) => {
    try {
      await syncAmazonOrderId(id);
      setSuccessFullModalOpen(true);
    } catch (error: any) {
      if (error?.status === 400) setModalOpen(true);
      notify(formateErrorObject(error), 'error');
    }
  };

  const handleContinue = async () => {
    if (!orderId.trim()) {
      setError('Please enter your order ID');
      return;
    }

    setLoading(true);

    try {
      await checkAmazonOrderId(orderId);

      if (!userData?.zl_uid) {
        showSignupModal();
      } else {
        handleSyncAmazonOrderId(orderId);
      }
    } catch (err: any) {
      const message = formateErrorObject(err);
      if (err?.status === 400) setModalOpen(true);
      setError(message);
      console.error('Error checking order ID:', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-start mt-[15vh] md:mt-0 lg:items-center justify-center px-4'>
      <div className='w-full max-w-3xl text-center'>
        <h1 className='text-white text-2xl text-start md:text-center md:text-[32px] font-semibold flex-wrap md:leading-10 mb-4'>
          Have you purchased Book from{' '}
          <span className='text-primary'>Amazon?</span>
        </h1>

        <p className='text-grayZl-500 font-light text-base text-start md:text-center md:text-lg'>
          You can redeem your digital Book for absolutely free.
        </p>

        <div className='flex flex-col md:flex-row gap-4 justify-center items-start my-8 md:my-10'>
          <div className='flex-1 w-full md:max-w-md'>
            <Input
              placeholder='Enter your amazon order id'
              value={orderId}
              onChange={handleInputChange}
              size='large'
              className='!h-12 !text-base border w-full border-l-grayZl-900 rounded-lg placeholder:text-grayZl-600'
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                fontSize: '16px',
              }}
              styles={{
                input: {
                  backgroundColor: 'transparent',
                  color: 'white',
                  fontSize: '16px',
                },
              }}
            />
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.3 }}
                  className='mt-2 inline-flex items-start justify-start w-full text-red-500 text-sm'
                >
                  <ExclamationCircleFilled className='text-red-500 inline mt-0.5 mr-2' />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <ButtonDefault
            size={4}
            rounded={false}
            roundedSm={true}
            disabled={!orderId.trim() || loading}
            onClick={(e) => {
              e.preventDefault();
              handleContinue();
            }}
            variant={ButtonVariants.PRIMARY}
            className='h-full w-full md:max-w-[140px] disabled:bg-grayZl-900 disabled:text-grayZl-700'
          >
            <span className='flex text-sm gap-x-2 h-full py-1 px-4 whitespace-nowrap'>
              {loading ? 'Loading...' : 'Continue'}
            </span>
          </ButtonDefault>
        </div>

        <div className='flex justify-center w-full'>
          <ButtonDefault
            size={0}
            rounded={false}
            roundedSm={true}
            onClick={() => setHowItWorkModalOpen(true)}
            variant={ButtonVariants.UNSTYLED}
            className='text-white hover:text-gray-300 text-base underline underline-offset-4'
          >
            <span className='flex text-sm gap-x-2 h-full py-1 px-4 whitespace-nowrap'>
              How it works?
            </span>
          </ButtonDefault>
        </div>

        <RedeemErrorModal
          isVisible={modalOpen}
          onClose={() => setModalOpen(false)}
          onRaiseConcern={() => router.push('/contact/')}
        />

        <SuccessModal
          isVisible={successFullModalOpen}
          onClose={() => setSuccessFullModalOpen(false)}
          onGoToLibrary={() => router.push('/library')}
        />

        <HowItWorksModal
          isVisible={howItWorkModalOpen}
          onClose={() => setHowItWorkModalOpen(false)}
          onClickVideo={() => {}}
        />
      </div>
    </div>
  );
};

export default ClaimBookPage;
